/**
 * Direct Google Analytics 4 (gtag.js) integration (#256).
 *
 * Decision (ADR 0008): send GA4 *recommended* events straight to GA via gtag —
 * no Google Tag Manager, no other dashboards — so popular searches/calibers/
 * product views land in GA4's built-in reports with zero manual tag setup.
 *
 * Consent is non-negotiable (CLAUDE.md rule 1, spec §10.7): analytics defaults
 * to denied (ADR 0003). We boot GA4 in Consent Mode v2 with `analytics_storage`
 * DENIED, load the gtag.js library + flip storage to GRANTED only after the user
 * opts in, and `gaEvent()` additionally drops any event while analytics consent
 * is not granted. With no `VITE_GA_MEASUREMENT_ID` configured, the whole thing
 * is a no-op (local/dev/tests never touch Google).
 */

import { useConsentStore } from '../../stores/consentStore';

/** GA4 Measurement ID (G-XXXXXXXXXX). Unset → GA disabled entirely. */
const MEASUREMENT_ID = (import.meta.env.VITE_GA_MEASUREMENT_ID as string | undefined) || '';

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
  }
}

/** Whether GA is configured for this build (a Measurement ID is present). */
export const analyticsConfigured = Boolean(MEASUREMENT_ID);

let scriptLoaded = false;

/** Create the gtag stub + dataLayer (idempotent). Only call when GA is configured. */
function ensureGtag(): void {
  if (typeof window === 'undefined') return;
  window.dataLayer = window.dataLayer ?? [];
  if (!window.gtag) {
    window.gtag = function gtag(...args: unknown[]) {
      window.dataLayer!.push(args);
    };
  }
}

/**
 * Boot GA4 in Consent Mode with analytics storage DENIED by default — call once
 * on app mount. No network request happens until {@link enableAnalytics}.
 */
export function initAnalytics(): void {
  if (!MEASUREMENT_ID || typeof window === 'undefined') return;
  ensureGtag();
  window.gtag!('consent', 'default', { analytics_storage: 'denied' });
  window.gtag!('js', new Date());
}

/**
 * Grant analytics storage and load the gtag.js library (once). Call when the
 * user has opted into analytics.
 */
export function enableAnalytics(): void {
  if (!MEASUREMENT_ID || typeof window === 'undefined') return;
  ensureGtag();
  window.gtag!('consent', 'update', { analytics_storage: 'granted' });
  if (scriptLoaded) return;
  scriptLoaded = true;
  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(MEASUREMENT_ID)}`;
  script.setAttribute('data-ga', MEASUREMENT_ID);
  document.head.appendChild(script);
  window.gtag!('config', MEASUREMENT_ID, { send_page_view: true });
}

/** Revoke analytics storage (consent withdrawn). The library can't be unloaded
 * mid-session, but Consent Mode stops it from using storage. */
export function disableAnalytics(): void {
  if (!MEASUREMENT_ID || typeof window === 'undefined') return;
  ensureGtag();
  window.gtag!('consent', 'update', { analytics_storage: 'denied' });
}

/**
 * Send a GA4 event. No-op unless GA is configured + loaded AND the user has
 * granted analytics consent (the strict gate — events never fire otherwise).
 * Undefined params are dropped so GA doesn't record empty values.
 *
 * @param name GA4 event name (prefer a recommended event)
 * @param params event parameters
 */
export function gaEvent(name: string, params?: Record<string, unknown>): void {
  if (typeof window === 'undefined' || !window.gtag) return;
  if (!useConsentStore.getState().analytics) return;
  const clean: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(params ?? {})) {
    if (v !== undefined && v !== null && v !== '') clean[k] = v;
  }
  window.gtag('event', name, clean);
}
